(function () {
  'use strict';

  const FIELD_LABELS = {
    shape: 'Shape',
    carat: 'Center stone weight',
    stoneType: 'Stone',
    naturalBudget: 'Natural Diamond budget',
    naturalSpecs: 'Natural Diamond specifications',
    metal: 'Metal',
    setting: 'Setting',
    prongs: 'Prongs',
    headStyle: 'Head',
    bandStyle: 'Band',
    bandWidth: 'Width',
    bandProfile: 'Profile',
    paveCoverage: 'Pavé',
    hiddenDetails: 'Hidden detail',
    engraving: 'Engraving',
    engravingMessage: 'Engraving message',
    ringSize: 'Ring size',
    ringSizeCustom: 'Custom size',
    contactMethod: 'Contact by',
    neededBy: 'Consultation timing'
  };

  function isValidInternationalPhone(value) {
    const normalized = String(value || '').trim();
    if (!/^\+?[0-9 ().-]+$/.test(normalized)) return false;
    const digitCount = (normalized.match(/\d/g) || []).length;
    if (digitCount < 7 || digitCount > 15) return false;
    let openParentheses = 0;
    for (const character of normalized) {
      if (character === '(') openParentheses += 1;
      if (character === ')') openParentheses -= 1;
      if (openParentheses < 0) return false;
    }
    return openParentheses === 0;
  }

  function estimateScopeDisclosure(state) {
    const natural = state.stoneType === 'Natural Diamond';
    if (state.setting === 'Three Stone') {
      return natural
        ? 'No online estimate is generated. Side-stone specifications and all Natural Diamond pricing are confirmed personally.'
        : 'Any displayed starting estimate covers the selected center stone and base configuration only; side-stone specifications and pricing are confirmed personally.';
    }
    if (state.setting === 'Toi et Moi') {
      return natural
        ? "No online estimate is generated. The second stone's specifications and all Natural Diamond pricing are confirmed personally."
        : "Any displayed starting estimate covers the selected center stone and base configuration only; the second stone's specifications and pricing are confirmed personally.";
    }
    return '';
  }

  const SUMMARY_FIELDS = ['shape', 'carat', 'stoneType', 'metal', 'setting', 'bandStyle', 'engraving', 'ringSize'];
  const TradeCore = window.FereGoldTradeCore || null;
  const QUESTION_STEP_COUNT = 9;
  const REVIEW_STEP_INDEX = 9;
  const CONTACT_STEP_INDEX = 8;
  const INSPIRATION_STEP_INDEX = 7;
  const SUBMISSION_STORAGE_KEY = 'fere_custom_ring_submission_v3';
  const ESTIMATE_WEIGHT_GRAMS = 5;
  const CENTER_STONE_CENTS_PER_CARAT = 15000;
  const LABOR_CENTS = 30000;
  const DIAMOND_BAND_CENTS = 15000;
  const ENGRAVING_CENTS = 5000;
  const CUSTOMER_ADDITION_CENTS = 115000;
  const CUSTOM_PRICE_INCREASE_CENTS = 75000;
  const OFFER_CREDIT_CENTS = 10000;
  const PRICE_INCREMENT_CENTS = 10000;
  const EIGHTEEN_K_PRICE_STEP_CENTS = 20000;
  const PLATINUM_PRICE_STEP_CENTS = 60000;
  const MAX_QUOTE_AGE_MS = 15 * 60 * 1000;
  const VALID_SPOT_RANGE_USD = [500, 10000];
  const GOLD_REQUEST_TIMEOUT_MS = 8000;
  const usd = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  const moneyFromCents = function (cents) { return usd.format(Number(cents) / 100); };
  const wholeUsd = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
  const displayRoundedCents = function (cents) { return wholeUsd.format(Number(cents) / 100); };
  const moneyWithCents = function (cents) {
    return Number.isSafeInteger(cents) ? moneyFromCents(cents) + ' USD (' + cents + ' cents)' : 'Unknown';
  };
  const canonicalSpotUsd = function (value) {
    return Number.isFinite(Number(value)) ? Number(value).toFixed(8).replace(/\.?0+$/, '') : '';
  };
  const scrollIntoViewRespectingMotion = function (element, block) {
    if (!element) return;
    const reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: block || 'start' });
  };
  const formatCurrencyCents = function (cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Number(cents) / 100);
    } catch (_error) {
      return 'Unavailable';
    }
  };

  function fixOurStoryLinks() {
    const storyPath = '/pages/our-story';
    document.querySelectorAll('a[href="/pages/contact"], a[href="/pages/contact/"]').forEach(function (link) {
      if (link.textContent.trim() === 'Our Story') link.setAttribute('href', storyPath);
    });
  }

  function safeSessionStorage() {
    try { return window.sessionStorage; } catch (_error) { return null; }
  }

  function publishStandaloneEvent(name, payload) {
    (async function () {
      for (let attempt = 0; attempt < 24; attempt += 1) {
        if (typeof window.Shopify?.analytics?.publish === 'function') {
          try { await window.Shopify.analytics.publish(name, payload || {}); } catch (_error) {}
          return;
        }
        await new Promise(function (resolve) { window.setTimeout(resolve, 125); });
      }
    })();
  }

  function readSubmissionReceipt() {
    try {
      const value = JSON.parse(safeSessionStorage()?.getItem(SUBMISSION_STORAGE_KEY) || 'null');
      return value && typeof value === 'object' ? value : null;
    } catch (_error) { return null; }
  }

  function getSubmissionOutcome(root, receipt, href, now) {
    const url = new URL(href);
    const errors = Boolean(root.querySelector('[data-fcrb-form-errors]'));
    const nativeSuccess = Boolean(
      root.querySelector('[data-fere-native-success]')
        && root.querySelector('[data-fcrb-success]')
    );
    if (errors) return { show: false, confirmed: false, failed: true };
    const age = now - Number(receipt?.savedAt);
    const validReceipt = Boolean(receipt
      && /^[a-zA-Z0-9-]{12,80}$/.test(String(receipt.requestId || ''))
      && Number.isFinite(age) && age >= 0 && age < 24 * 60 * 60 * 1000
      && receipt.returnPath === url.pathname);
    const requestMarker = url.searchParams.get('fcrb_request');
    const markerMatches = !requestMarker || requestMarker === receipt?.requestId;
    const newlyConfirmed = validReceipt && receipt.status === 'pending'
      && age < 60 * 60 * 1000 && markerMatches && nativeSuccess;
    const form = root.querySelector('#FereCustomRingRequestForm');
    const confirmedReturn = validReceipt && receipt.status === 'confirmed'
      && ((url.searchParams.get('fcrb_state') === 'consultation'
        && url.searchParams.get('fcrb_receipt') === receipt.requestId)
        || form?.dataset.fcrbSubmissionId === receipt.requestId);
    return {
      show: nativeSuccess || newlyConfirmed || confirmedReturn,
      confirmed: newlyConfirmed || confirmedReturn,
      newlyConfirmed: newlyConfirmed,
      failed: false
    };
  }

  function bookingCreditIsEligible(root, receipt, now) {
    const expiry = Number(receipt?.offerEndsAt);
    const submittedAt = Number(receipt?.submittedAt);
    return Boolean(receipt?.status === 'confirmed'
      && receipt.stoneType === 'Lab-grown diamond'
      && receipt.estimateValid === true && receipt.offerEnabled === true
      && root.dataset.fcrbOfferEnabled === 'true'
      && /^\$[\d,]+$/.test(String(receipt.total || ''))
      && /^\$[\d,]+$/.test(String(receipt.afterCredit || ''))
      && Number.isFinite(submittedAt) && Number.isFinite(expiry)
      && expiry > submittedAt && expiry <= submittedAt + 13 * 60 * 60 * 1000
      && expiry > now);
  }

  function initializeSubmittedRequest(root, success, receipt) {
    root.classList.add('has-submitted');
    root.classList.remove('is-final-review');
    document.body.classList.remove('fcrb-summary-open');
    root.querySelectorAll('.fcrb-entry, .fcrb-hero, .fcrb-journey').forEach(function (element) {
      element.hidden = true;
    });
    const intro = success.querySelector('[data-fcrb-booking-intro]');
    if (intro && receipt?.status === 'confirmed') {
      intro.textContent = 'Your ring request has been received. Congratulations on taking the next step. Choose a time to discuss your design with a FERE jeweler.';
    }
    const credit = success.querySelector('[data-fcrb-booking-credit]');
    let anchorMs = Math.max(Date.now(), Number(root.dataset.fcrbServerEpoch) * 1000 || 0);
    let anchorPerformance = window.performance.now();
    const renderCredit = function () {
      if (!credit) return;
      const now = anchorMs + (window.performance.now() - anchorPerformance);
      const eligible = bookingCreditIsEligible(root, receipt, now);
      credit.hidden = !eligible;
      if (eligible) {
        const expiry = new Intl.DateTimeFormat('en-US', {
          timeZone: root.dataset.fcrbStoreTimezone || 'America/Chicago',
          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
        }).format(new Date(Number(receipt.offerEndsAt)));
        credit.textContent = 'Book before ' + expiry + ' to receive your eligible $100 Custom Design Credit.';
      } else {
        credit.textContent = '';
      }
    };
    renderCredit();
    if (root.dataset.fcrbBookingReady !== 'true') {
      root.dataset.fcrbBookingReady = 'true';
      success.querySelectorAll('[data-fcrb-consultation]').forEach(function (link) {
        link.addEventListener('click', function () {
          publishStandaloneEvent('fere_consultation_cta_clicked', {
            source: 'custom_ring_builder', context: 'submitted-request'
          });
        });
      });
      const timerId = window.setInterval(renderCredit, 10000);
      document.addEventListener('visibilitychange', renderCredit);
      window.addEventListener('pageshow', renderCredit);
      document.addEventListener('shopify:section:unload', function cleanup(event) {
        if (event.target !== root && !event.target?.contains?.(root)) return;
        window.clearInterval(timerId);
        document.removeEventListener('visibilitychange', renderCredit);
        window.removeEventListener('pageshow', renderCredit);
        document.removeEventListener('shopify:section:unload', cleanup);
      });
      if (receipt?.status === 'confirmed' && credit) {
        const rootPath = window.Shopify?.routes?.root || '/';
        fetch(rootPath + 'cart.js', { method: 'GET', cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (response) {
            const serverDate = Date.parse(response.headers.get('date') || '');
            if (!Number.isFinite(serverDate)) return;
            anchorMs = serverDate;
            anchorPerformance = window.performance.now();
            renderCredit();
          }).catch(function () {});
      }
    }
    window.requestAnimationFrame(function () {
      success.focus({ preventScroll: true });
      scrollIntoViewRespectingMotion(success, 'start');
    });
  }

  function restoreSubmittedRequest(root) {
    let receipt = readSubmissionReceipt();
    const outcome = getSubmissionOutcome(root, receipt, window.location.href, Date.now());
    if (outcome.failed) {
      try { safeSessionStorage()?.removeItem(SUBMISSION_STORAGE_KEY); } catch (_error) {}
      return false;
    }
    if (!outcome.show) return false;
    let success = root.querySelector('[data-fcrb-success]');
    if (!success) {
      const template = root.querySelector('[data-fcrb-success-template]');
      const form = root.querySelector('#FereCustomRingRequestForm');
      const content = template?.content?.firstElementChild;
      if (!form || !content) return false;
      success = content.cloneNode(true);
      form.replaceChildren(success);
    }
    if (outcome.newlyConfirmed) {
      receipt = { ...receipt, status: 'confirmed', confirmedAt: Date.now() };
      try { safeSessionStorage()?.setItem(SUBMISSION_STORAGE_KEY, JSON.stringify(receipt)); } catch (_error) {}
    }
    const confirmedReceipt = outcome.confirmed ? receipt : null;
    if (confirmedReceipt) {
      try {
        const stored = readSubmissionReceipt();
        if (stored?.status === 'confirmed' && stored.requestId === confirmedReceipt.requestId) {
          const address = new URL(window.location.href);
          address.searchParams.delete('fcrb_request');
          address.searchParams.set('view', 'fere-consultation');
          address.searchParams.set('fcrb_state', 'consultation');
          address.searchParams.set('fcrb_receipt', confirmedReceipt.requestId);
          address.hash = 'FereConsultationConfirmation';
          window.history.replaceState(window.history.state, '', address.pathname + address.search + address.hash);
        }
      } catch (_error) {}
    }
    initializeSubmittedRequest(root, success, confirmedReceipt);
    return true;
  }

  function initBuilder(root) {
    if (!root || root.dataset.fcrbReady === 'true') return;
    root.dataset.fcrbReady = 'true';

    const form = root.querySelector('#FereCustomRingRequestForm');
    if (restoreSubmittedRequest(root)) return;

    if (!form) return;

    const steps = Array.from(root.querySelectorAll('[data-fcrb-step]')).sort(function (firstStep, secondStep) {
      return Number(firstStep.dataset.fcrbStep) - Number(secondStep.dataset.fcrbStep);
    });
    const nextButton = root.querySelector('[data-fcrb-next]');
    const navigation = root.querySelector('.fcrb-navigation');
    const backButton = root.querySelector('[data-fcrb-back]');
    const progressCount = root.querySelector('.fcrb-progress__count');
    const progressBar = root.querySelector('[data-fcrb-progress-bar]');
    const stepError = root.querySelector('[data-fcrb-step-error]');
    const summary = root.querySelector('[data-fcrb-summary]');
    const summaryList = root.querySelector('[data-fcrb-summary-list]');
    const summaryTitle = root.querySelector('[data-fcrb-summary-title]');
    const summaryStone = root.querySelector('[data-fcrb-summary-stone]');
    const summaryInput = root.querySelector('[data-fcrb-summary-input]');
    const mobileSummaryButton = root.querySelector('[data-fcrb-mobile-summary]');
    const summaryCloseButton = root.querySelector('[data-fcrb-summary-close]');
    const reviewList = root.querySelector('[data-fcrb-review-list]');
    const reviewContact = root.querySelector('[data-fcrb-review-contact]');
    const reviewLead = root.querySelector('[data-fcrb-review-lead]');
    const certificationTitle = root.querySelector('[data-fcrb-certification-title]');
    const certificationCopy = root.querySelector('[data-fcrb-certification-copy]');
    const builder = root.querySelector('#FereRingRequestBuilder');
    const livePreview = root.querySelector('[data-fcrb-live-preview]');
    const liveSettingImage = root.querySelector('[data-fcrb-live-setting-image]');
    const liveEmpty = root.querySelector('[data-fcrb-live-empty]');
    const liveEmptyCopy = root.querySelector('[data-fcrb-live-empty-copy]');
    const liveMetal = root.querySelector('[data-fcrb-live-metal]');
    const liveMetalLabel = root.querySelector('[data-fcrb-live-metal-label]');
    const liveTitle = root.querySelector('[data-fcrb-live-title]');
    const liveSubtitle = root.querySelector('[data-fcrb-live-subtitle]');
    const liveBand = root.querySelector('[data-fcrb-live-band]');
    const liveBandImage = root.querySelector('[data-fcrb-live-band-image]');
    const liveBandLabel = root.querySelector('[data-fcrb-live-band-label]');
    const paveOptions = root.querySelector('[data-fcrb-pave-options]');
    const engravingField = root.querySelector('[data-fcrb-engraving-field]');
    const engravingInput = root.querySelector('[data-fcrb-input="engravingMessage"]');
    const customSizeField = root.querySelector('[data-fcrb-custom-size]');
    const customSizeInput = root.querySelector('[data-fcrb-input="ringSizeCustom"]');
    const naturalDetails = root.querySelector('[data-fcrb-natural-details]');
    const naturalBudgetInput = root.querySelector('[data-fcrb-input="naturalBudget"]');
    const naturalSpecsInput = root.querySelector('[data-fcrb-input="naturalSpecs"]');
    const settingDetails = root.querySelector('[data-fcrb-setting-details]');
    const multiStoneNote = root.querySelector('[data-fcrb-multi-stone-note]');
    const estimateScopeInput = root.querySelector('[data-fcrb-estimate-scope]');
    const estimatePending = root.querySelector('[data-fcrb-estimate-pending]');
    const estimateResult = root.querySelector('[data-fcrb-estimate-result]');
    const naturalQuote = root.querySelector('[data-fcrb-natural-quote]');
    const personalQuote = root.querySelector('[data-fcrb-personal-quote]');
    const personalQuoteCopy = root.querySelector('[data-fcrb-personal-quote-copy]');
    const estimateTotal = root.querySelector('[data-fcrb-estimate-total]');
    const estimateAfterCredit = root.querySelector('[data-fcrb-estimate-after-credit]');
    const estimateOffer = root.querySelector('[data-fcrb-estimate-offer]');
    const estimateAnnouncement = root.querySelector('[data-fcrb-estimate-announcement]');
    const offerTimer = root.querySelector('[data-fcrb-offer-timer]');
    const offerEndLabel = root.querySelector('[data-fcrb-offer-end-label]');
    const state = {};
    const storedTradeContext = TradeCore ? TradeCore.readContext((function () {
      try { return window.sessionStorage; } catch (_error) { return null; }
    })()) : null;
    let tradeContext = storedTradeContext?.path === 'custom-ring-trade' ? storedTradeContext : null;
    let currentStep = 0;
    let pendingSubmitEvent = null;
    let reviewEditStep = null;
    let reviewEditEndStep = null;
    let goldQuote = null;
    let goldQuoteState = 'pending';
    let goldQuoteLoadPromise = null;
    let goldQuoteRefreshTimerId = null;
    let offerTimerId = null;
    let offerWindowEndMs = null;
    let offerActive = false;
    let disposed = false;
    let clockAnchorMs = Number(root.dataset.fcrbServerEpoch) * 1000;
    let clockAnchorPerformance = window.performance.now();
    const trackedEvents = new Set();
    const quoteAbortControllers = new Set();
    const offerEnabled = root.dataset.fcrbOfferEnabled === 'true';
    const storeTimeZone = root.dataset.fcrbStoreTimezone || 'America/Chicago';
    const mobileSummaryMedia = window.matchMedia('(max-width: 989px)');
    const mobileSummaryFocusSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    let mobileSummaryInertedElements = [];
    const contactValueCache = new Map();
    const contactValueNames = ['contact[name]', 'contact[phone]', 'contact[email]', 'contact[Needed-by timeframe]', 'contact[Additional notes]'];
    const returnedFormBody = root.querySelector('[data-fcrb-form-errors]')
      ? String(form.querySelector('[data-fcrb-contact-body]')?.value || '').replace(/\r\n?/g, '\n').slice(0, 30000)
      : '';

    function publishBuilderEvent(name, payload, dedupeKey) {
      const key = dedupeKey || name;
      if (trackedEvents.has(key)) return;
      trackedEvents.add(key);
      (async function () {
        for (let attempt = 0; attempt < 24; attempt += 1) {
          if (disposed) return;
          if (typeof window.Shopify?.analytics?.publish === 'function') {
            try {
              await window.Shopify.analytics.publish(name, payload || {});
            } catch (_error) {}
            return;
          }
          await new Promise(function (resolve) { window.setTimeout(resolve, 125); });
        }
      })();
    }

    function setEstimateField(key, value) {
      const field = form.querySelector('[data-fcrb-estimate-field="' + key + '"]');
      if (field) field.value = value;
    }

    function setClockAnchor(milliseconds) {
      if (!Number.isFinite(milliseconds) || milliseconds <= 0) return;
      clockAnchorMs = milliseconds;
      clockAnchorPerformance = window.performance.now();
      offerWindowEndMs = null;
      renderOfferTimer();
    }

    function authoritativeNowMs() {
      if (Number.isFinite(clockAnchorMs) && clockAnchorMs > 0) {
        return clockAnchorMs + (window.performance.now() - clockAnchorPerformance);
      }
      return Date.now();
    }

    async function syncServerClock() {
      const rootPath = window.Shopify?.routes?.root || '/';
      try {
        const response = await fetch(rootPath + 'cart.js', {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        const serverDate = Date.parse(response.headers.get('date') || '');
        if (Number.isFinite(serverDate)) setClockAnchor(serverDate);
      } catch (_error) {}
    }

    function zonedDateParts(milliseconds) {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: storeTimeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      }).formatToParts(new Date(milliseconds));
      return parts.reduce(function (values, part) {
        if (part.type !== 'literal') values[part.type] = Number(part.value);
        return values;
      }, {});
    }

    function zonedLocalToEpoch(parts) {
      const targetAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute || 0, parts.second || 0);
      let guess = targetAsUtc;
      for (let count = 0; count < 4; count += 1) {
        const observed = zonedDateParts(guess);
        const observedAsUtc = Date.UTC(observed.year, observed.month - 1, observed.day, observed.hour, observed.minute, observed.second);
        guess += targetAsUtc - observedAsUtc;
      }
      return guess;
    }

    function nextOfferWindowEnd(milliseconds) {
      const local = zonedDateParts(milliseconds);
      if (local.hour < 12) {
        return zonedLocalToEpoch({
          year: local.year,
          month: local.month,
          day: local.day,
          hour: 12,
          minute: 0,
          second: 0
        });
      }
      const nextDay = new Date(Date.UTC(local.year, local.month - 1, local.day + 1));
      return zonedLocalToEpoch({
        year: nextDay.getUTCFullYear(),
        month: nextDay.getUTCMonth() + 1,
        day: nextDay.getUTCDate(),
        hour: 0,
        minute: 0,
        second: 0
      });
    }

    function renderOfferTimer() {
      if (!offerEnabled || !offerActive || !offerTimer || disposed) return;
      const now = authoritativeNowMs();
      if (!Number.isFinite(offerWindowEndMs) || now >= offerWindowEndMs) {
        offerWindowEndMs = nextOfferWindowEnd(now);
        const endDate = new Date(offerWindowEndMs);
        const formattedEnd = new Intl.DateTimeFormat('en-US', {
          timeZone: storeTimeZone,
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short'
        }).format(endDate);
        if (offerEndLabel) offerEndLabel.textContent = 'This offer window ends ' + formattedEnd + '.';
      }
      setEstimateField('offerEnds', new Date(offerWindowEndMs).toISOString());
      const remainingSeconds = Math.max(0, Math.ceil((offerWindowEndMs - now) / 1000));
      const hours = Math.floor(remainingSeconds / 3600);
      const minutes = Math.floor((remainingSeconds % 3600) / 60);
      const seconds = remainingSeconds % 60;
      offerTimer.textContent = [hours, minutes, seconds].map(function (value) {
        return String(value).padStart(2, '0');
      }).join(':');
      offerTimer.setAttribute('aria-label', hours + ' hours, ' + minutes + ' minutes remaining in this store offer window');
    }

    function startOfferTimer() {
      if (!offerEnabled) return;
      renderOfferTimer();
      if (offerTimerId !== null) return;
      offerTimerId = window.setInterval(renderOfferTimer, 1000);
    }

    function validQuoteTimestamp(value) {
      const milliseconds = Date.parse(String(value || ''));
      if (!Number.isFinite(milliseconds)) return null;
      const age = authoritativeNowMs() - milliseconds;
      if (age < 0 || age > MAX_QUOTE_AGE_MS) return null;
      return { value: String(value), milliseconds: milliseconds, status: 'live' };
    }

    function validSpot(value) {
      const spot = Number(value);
      return Number.isFinite(spot) && spot >= VALID_SPOT_RANGE_USD[0] && spot <= VALID_SPOT_RANGE_USD[1]
        ? spot
        : null;
    }

    function normalizeGoldApiQuote(payload) {
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
      if (String(payload.symbol || '').toUpperCase() !== 'XAU') return null;
      if (String(payload.currency || '').toUpperCase() !== 'USD') return null;
      const spotUsd = validSpot(payload.price);
      const timestamp = validQuoteTimestamp(payload.updatedAt);
      if (spotUsd === null || !timestamp) return null;
      return {
        spotUsd: spotUsd,
        status: timestamp.status,
        source: 'Gold-API.com',
        timestamp: timestamp.value
      };
    }

    function normalizeXausQuote(payload) {
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
      const symbol = String(payload.symbol || (payload.xau ? 'XAU' : '')).toUpperCase();
      const currency = String(payload?.xau?.currency || '').toUpperCase();
      const unit = String(payload?.xau?.unit || '').toLowerCase();
      const spotUsd = validSpot(payload?.xau?.price ?? payload?.spot_usd_oz);
      const timestamp = validQuoteTimestamp(payload?.data_state?.as_of || payload?.price_as_of);
      const dataStatus = String(payload?.data_state?.status || '').toLowerCase();
      if (symbol !== 'XAU' || currency !== 'USD' || !['troy_oz', 'oz'].includes(unit)) return null;
      if (spotUsd === null || !timestamp || ['unavailable', 'stale'].includes(dataStatus)) return null;
      return {
        spotUsd: spotUsd,
        status: timestamp.status,
        source: String(payload?.price_source || payload?.data_state?.source || payload?.source || 'XAUS.com'),
        timestamp: timestamp.value
      };
    }

    async function requestGoldJson(url) {
      const controller = new AbortController();
      quoteAbortControllers.add(controller);
      const timeout = window.setTimeout(function () { controller.abort(); }, GOLD_REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-store',
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          signal: controller.signal
        });
        if (!response.ok) throw new Error('gold_quote_http_' + response.status);
        return await response.json();
      } finally {
        window.clearTimeout(timeout);
        quoteAbortControllers.delete(controller);
      }
    }

    async function loadGoldQuote() {
      const primaryUrl = 'https://api.gold-api.com/price/XAU';
      const retryDelays = [0, 2000, 8000];
      for (let index = 0; index < retryDelays.length; index += 1) {
        if (disposed) return;
        if (retryDelays[index]) await new Promise(function (resolve) { window.setTimeout(resolve, retryDelays[index]); });
        try {
          const primary = normalizeGoldApiQuote(await requestGoldJson(primaryUrl));
          if (primary) {
            goldQuote = primary;
            goldQuoteState = 'ready';
            scheduleGoldQuoteRefresh();
            renderEstimate();
            return;
          }
        } catch (_error) {}
      }

      if (disposed) return;
      try {
        const fallbackUrl = 'https://xaus.com/api/v1/spot?compact=1&currency=USD&unit=oz&fresh=' + Math.floor(authoritativeNowMs() / 30000);
        const fallback = normalizeXausQuote(await requestGoldJson(fallbackUrl));
        if (fallback) {
          goldQuote = fallback;
          goldQuoteState = 'ready';
          scheduleGoldQuoteRefresh();
          renderEstimate();
          return;
        }
      } catch (_error) {}

      goldQuote = null;
      goldQuoteState = 'unavailable';
      renderEstimate();
    }

    function scheduleGoldQuoteRefresh() {
      if (goldQuoteRefreshTimerId !== null) window.clearTimeout(goldQuoteRefreshTimerId);
      goldQuoteRefreshTimerId = null;
      const quoteMilliseconds = Date.parse(String(goldQuote?.timestamp || ''));
      if (!Number.isFinite(quoteMilliseconds)) return;
      const refreshDelay = Math.max(1000, quoteMilliseconds + MAX_QUOTE_AGE_MS - authoritativeNowMs());
      goldQuoteRefreshTimerId = window.setTimeout(function () {
        goldQuoteRefreshTimerId = null;
        goldQuote = null;
        goldQuoteState = 'pending';
        renderEstimate();
        void ensureFreshGoldQuote();
      }, refreshDelay);
    }

    function ensureFreshGoldQuote() {
      if (goldQuoteState === 'ready' && goldQuote && validQuoteTimestamp(goldQuote.timestamp)) {
        scheduleGoldQuoteRefresh();
        return Promise.resolve();
      }
      if (goldQuoteLoadPromise) return goldQuoteLoadPromise;
      goldQuote = null;
      goldQuoteState = 'pending';
      renderEstimate();
      goldQuoteLoadPromise = loadGoldQuote().finally(function () { goldQuoteLoadPromise = null; });
      return goldQuoteLoadPromise;
    }

    function clearChoiceField(field) {
      state[field] = '';
      root.querySelectorAll('[data-fcrb-field="' + field + '"]').forEach(function (choice) {
        choice.classList.remove('is-selected');
        choice.setAttribute('aria-pressed', 'false');
      });
      const input = root.querySelector('[data-fcrb-input="' + field + '"]');
      if (input) input.value = '';
    }

    function selectedChoiceImage(field, value) {
      if (!value) return '';
      const choice = Array.from(root.querySelectorAll('[data-fcrb-field="' + field + '"]')).find(function (candidate) {
        return candidate.dataset.value === value;
      });
      const image = choice?.querySelector('img');
      return image?.currentSrc || image?.src || '';
    }

    function updateLivePreview() {
      if (!livePreview) return;

      const settingUrl = selectedChoiceImage('setting', state.setting);
      if (liveSettingImage) {
        if (settingUrl) {
          if (liveSettingImage.src !== settingUrl) liveSettingImage.src = settingUrl;
          liveSettingImage.alt = state.setting + ' setting design direction';
          liveSettingImage.hidden = false;
        } else {
          liveSettingImage.hidden = true;
          liveSettingImage.removeAttribute('src');
          liveSettingImage.alt = '';
        }
      }
      if (liveEmpty) liveEmpty.hidden = Boolean(settingUrl);
      if (liveEmptyCopy) {
        liveEmptyCopy.textContent = state.shape
          ? 'Choose a setting for your ' + state.shape.toLowerCase() + ' center stone.'
          : 'Choose a setting to begin.';
      }

      if (summaryStone) summaryStone.hidden = !state.shape;

      const metalTone = /Yellow Gold/.test(state.metal)
        ? 'is-yellow'
        : /Rose Gold/.test(state.metal)
          ? 'is-rose'
          : /White Gold/.test(state.metal)
            ? 'is-white'
            : state.metal === 'Platinum'
              ? 'is-platinum'
              : 'is-neutral';
      const metalVisuals = [liveSettingImage].concat(Array.from(root.querySelectorAll('[data-fcrb-setting-image]'))).filter(Boolean);
      metalVisuals.forEach(function (image) {
        image.classList.remove('is-yellow', 'is-rose', 'is-white', 'is-platinum', 'is-neutral');
        image.classList.add(metalTone);
      });
      if (liveMetal) {
        liveMetal.classList.remove('is-yellow', 'is-rose', 'is-white', 'is-platinum', 'is-neutral');
        liveMetal.classList.add(metalTone);
        liveMetal.hidden = !state.metal;
      }
      if (liveMetalLabel) liveMetalLabel.textContent = state.metal || 'Metal';

      const titleParts = [state.shape, state.setting].filter(Boolean);
      if (liveTitle) liveTitle.textContent = titleParts.length ? titleParts.join(' · ') : 'Your ring, taking shape';
      const detailParts = [];
      if (state.carat) detailParts.push(formatCarat(state.carat));
      if (state.metal) detailParts.push(state.metal);
      if (state.bandStyle) detailParts.push(state.bandStyle);
      if (liveSubtitle) {
        liveSubtitle.textContent = detailParts.length
          ? detailParts.join(' · ')
          : 'Select a center shape and setting to begin.';
      }

      const bandUrl = selectedChoiceImage('bandStyle', state.bandStyle);
      if (liveBand) liveBand.hidden = !state.bandStyle;
      if (liveBandLabel) liveBandLabel.textContent = state.bandStyle || 'Not selected';
      if (liveBandImage) {
        if (bandUrl) {
          if (liveBandImage.src !== bandUrl) liveBandImage.src = bandUrl;
          liveBandImage.alt = state.bandStyle + ' design direction';
          liveBandImage.hidden = false;
        } else {
          liveBandImage.hidden = true;
          liveBandImage.removeAttribute('src');
          liveBandImage.alt = '';
        }
      }

      livePreview.classList.toggle('has-setting', Boolean(settingUrl));
      livePreview.classList.toggle('has-shape', Boolean(state.shape));
      livePreview.classList.toggle('has-metal', Boolean(state.metal));
      livePreview.classList.toggle('has-band', Boolean(state.bandStyle));
    }

    function syncConditionalFields() {
      const showNaturalDetails = state.stoneType === 'Natural Diamond';
      if (naturalDetails) naturalDetails.hidden = !showNaturalDetails;
      [naturalBudgetInput, naturalSpecsInput].forEach(function (input) {
        if (input) input.disabled = !showNaturalDetails;
      });
      if (!showNaturalDetails) {
        ['naturalBudget', 'naturalSpecs'].forEach(function (field) {
          const input = root.querySelector('[data-fcrb-input="' + field + '"]');
          state[field] = '';
          if (input) input.value = '';
        });
      }

      const bezelSelected = state.setting === 'Bezel';
      if (settingDetails) {
        settingDetails.hidden = bezelSelected;
        if (bezelSelected) settingDetails.open = false;
      }
      if (bezelSelected) {
        if (state.prongs) clearChoiceField('prongs');
        if (state.headStyle) clearChoiceField('headStyle');
      }

      const scopeDisclosure = estimateScopeDisclosure(state);
      if (multiStoneNote) {
        multiStoneNote.hidden = !scopeDisclosure;
        multiStoneNote.textContent = scopeDisclosure;
      }
      if (estimateScopeInput) {
        estimateScopeInput.value = scopeDisclosure;
        estimateScopeInput.disabled = !scopeDisclosure;
      }

      const showPave = state.bandStyle === 'Diamond Band';
      if (paveOptions) paveOptions.hidden = !showPave;
      if (!showPave && state.paveCoverage) clearChoiceField('paveCoverage');

      const showEngraving = state.engraving === 'Add engraving (+$50)';
      if (engravingField) engravingField.hidden = !showEngraving;
      if (engravingInput) engravingInput.disabled = !showEngraving;
      if (!showEngraving && (state.engravingMessage || engravingInput?.value)) {
        state.engravingMessage = '';
        if (engravingInput) engravingInput.value = '';
      }

      const showCustomSize = state.ringSize === 'Custom size';
      if (customSizeField) customSizeField.hidden = !showCustomSize;
      if (customSizeInput) customSizeInput.disabled = !showCustomSize;
      if (!showCustomSize && (state.ringSizeCustom || customSizeInput?.value)) {
        state.ringSizeCustom = '';
        if (customSizeInput) customSizeInput.value = '';
      }
      updateLivePreview();
    }

    function resetEstimatePresentation(status) {
      if (estimatePending) estimatePending.hidden = true;
      if (estimateResult) estimateResult.hidden = true;
      if (naturalQuote) naturalQuote.hidden = true;
      if (personalQuote) personalQuote.hidden = true;
      if (estimateTotal) estimateTotal.textContent = '—';
      if (estimateAfterCredit) estimateAfterCredit.textContent = '—';
      setEstimateField('total', '');
      setEstimateField('afterCredit', '');
      setEstimateField('status', status || 'Pending');
      setEstimateField('source', 'Not applicable');
      setEstimateField('timestamp', 'Not applicable');
      setEstimateField('offerEnds', offerEnabled ? 'Not applicable' : 'Offer disabled');
      offerActive = false;
    }

    function renderPersonalQuote(copy, status) {
      resetEstimatePresentation(status);
      if (personalQuoteCopy) personalQuoteCopy.textContent = copy;
      if (personalQuote) personalQuote.hidden = false;
      if (estimateAnnouncement) estimateAnnouncement.textContent = copy;
    }

    function selectedKarat() {
      const match = String(state.metal || '').match(/^(10|14|18)K\s/);
      return match ? Number(match[1]) : null;
    }

    function selectedMetalPricing() {
      const karat = selectedKarat();
      if (state.metal === 'Platinum') {
        return { referenceKarat: 14, premiumCents: PLATINUM_PRICE_STEP_CENTS, tier: 'platinum' };
      }
      if (karat === 18) {
        return { referenceKarat: 14, premiumCents: EIGHTEEN_K_PRICE_STEP_CENTS, tier: '18k' };
      }
      if (karat) {
        return { referenceKarat: karat, premiumCents: 0, tier: String(karat) + 'k' };
      }
      return null;
    }

    function parseCaratWeight(value) {
      const normalized = String(value || '').trim();
      if (!/^\d(?:\.5)?$/.test(normalized)) return null;
      const caratWeight = Number(normalized);
      return [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].includes(caratWeight) ? caratWeight : null;
    }

    function roundToNearestHundredDollars(cents) {
      return Math.round(cents / PRICE_INCREMENT_CENTS) * PRICE_INCREMENT_CENTS;
    }

    function renderEstimate() {
      if (!estimatePending || !estimateResult || !naturalQuote || !personalQuote) return;

      if (state.stoneType === 'Natural Diamond') {
        resetEstimatePresentation('Natural Diamond — direct quote required');
        naturalQuote.hidden = false;
        if (estimateAnnouncement) estimateAnnouncement.textContent = 'Natural Diamond pricing requires a personalized quote.';
        publishBuilderEvent('fere_natural_quote_path', { source: 'custom_ring_builder' }, 'natural_quote_path');
        return;
      }

      if (state.stoneType && state.stoneType !== 'Lab-grown diamond') {
        renderPersonalQuote('Your selected center stone is best priced personally by a FERE specialist.', 'Personalized quote required');
        return;
      }

      const caratWeight = parseCaratWeight(state.carat);
      if (state.stoneType === 'Lab-grown diamond' && caratWeight === null) {
        renderPersonalQuote('Choose a center stone carat weight to receive your Lab-Grown Quick Estimate.', 'Center stone carat required for Quick Estimate');
        return;
      }

      const metalPricing = selectedMetalPricing();
      if (state.stoneType === 'Lab-grown diamond' && !metalPricing) {
        renderPersonalQuote('Choose 10K, 14K, 18K gold, or Platinum for an instant estimate, or continue for a personalized quote.', 'Selected metal requires a personalized quote');
        return;
      }

      if (state.stoneType !== 'Lab-grown diamond') {
        resetEstimatePresentation('Pending configuration');
        estimatePending.hidden = false;
        return;
      }

      if (goldQuoteState === 'pending') {
        resetEstimatePresentation('Current gold pricing pending');
        estimatePending.hidden = false;
        return;
      }

      if (goldQuoteState !== 'ready' || !goldQuote || !validQuoteTimestamp(goldQuote.timestamp) || !TradeCore) {
        renderPersonalQuote('Current gold pricing is temporarily unavailable. A FERE specialist can prepare your personalized quote.', 'Current gold pricing unavailable');
        return;
      }

      const gold = TradeCore.calculateEstimate({
        spotUsd: goldQuote.spotUsd,
        weightGrams: ESTIMATE_WEIGHT_GRAMS,
        karat: metalPricing.referenceKarat
      });
      if (!gold) {
        renderPersonalQuote('Current gold pricing is temporarily unavailable. A FERE specialist can prepare your personalized quote.', 'Current gold pricing unavailable');
        return;
      }

      const centerStoneCents = Math.round(caratWeight * CENTER_STONE_CENTS_PER_CARAT);
      const internalEstimateCents = centerStoneCents
        + gold.marketCents
        + LABOR_CENTS
        + metalPricing.premiumCents
        + (state.bandStyle === 'Diamond Band' ? DIAMOND_BAND_CENTS : 0)
        + (state.engraving === 'Add engraving (+$50)' ? ENGRAVING_CENTS : 0);
      const originalEstimateCents = roundToNearestHundredDollars(internalEstimateCents + CUSTOMER_ADDITION_CENTS);
      const applicableCreditCents = offerEnabled ? OFFER_CREDIT_CENTS : 0;
      const customerEstimateCents = originalEstimateCents + CUSTOM_PRICE_INCREASE_CENTS + applicableCreditCents;
      const afterCreditCents = customerEstimateCents - applicableCreditCents;
      const customerDisplay = displayRoundedCents(customerEstimateCents);
      const afterCreditDisplay = displayRoundedCents(afterCreditCents);

      resetEstimatePresentation('Lab-Grown Quick Estimate displayed');
      estimateResult.hidden = false;
      estimateTotal.textContent = customerDisplay;
      setEstimateField('total', customerDisplay + ' USD');
      setEstimateField('source', goldQuote.source + ' · ' + goldQuote.status);
      setEstimateField('timestamp', goldQuote.timestamp);

      if (offerEnabled) {
        estimateOffer.hidden = false;
        estimateAfterCredit.textContent = afterCreditDisplay;
        setEstimateField('afterCredit', afterCreditDisplay + ' USD');
        offerActive = true;
        startOfferTimer();
      } else {
        estimateOffer.hidden = true;
        setEstimateField('afterCredit', 'Offer disabled');
      }

      if (estimateAnnouncement) estimateAnnouncement.textContent = 'Your estimated ring total is ' + customerDisplay + '.';
      const signature = [caratWeight, metalPricing.tier, state.bandStyle, state.engraving, customerEstimateCents].join('|');
      if (currentStep === REVIEW_STEP_INDEX) {
        publishBuilderEvent('fere_estimate_displayed', {
          source: 'custom_ring_builder',
          stone_type: 'lab_grown',
          offer_enabled: offerEnabled
        }, 'estimate:' + signature);
      }
    }

    function setTradeField(key, value) {
      const field = form.querySelector('[data-fcrb-trade-field="' + key + '"]');
      if (field) field.value = value;
    }

    function refreshTradeContext() {
      if (!tradeContext || !TradeCore) return tradeContext;
      tradeContext = TradeCore.sanitizeContext(tradeContext, Date.now());
      return tradeContext;
    }

    function tradeSummaryLines() {
      refreshTradeContext();
      if (!tradeContext) return [];
      const estimate = tradeContext.estimate;
      const quote = tradeContext.quote;
      const ring = tradeContext.ring;
      const contribution = tradeContext.contribution;
      return [
        'GOLD TRADE CONTEXT: CLIENT-SIDE UNVERIFIED ESTIMATE',
        'Origin: Sell Your Gold',
        'Path: ' + tradeContext.path,
        'Entered payable gold weight: ' + (tradeContext.gold.weightGrams === null ? 'Unknown' : tradeContext.gold.weightGrams + ' grams'),
        'Karat: ' + (tradeContext.gold.karat === null ? 'Unknown' : tradeContext.gold.karat + 'K'),
        'Estimate status: ' + (quote ? (quote.status === 'fresh' ? 'Live when carried' : 'Last available / stale') : 'Unavailable; revalidation required'),
        'Spot used in calculation: ' + (quote ? canonicalSpotUsd(quote.spotUsd) + ' USD per troy ounce' : 'Unknown'),
        'Source: ' + (quote ? quote.source + ' (' + quote.provider + ')' : 'Unknown'),
        'Quote timestamp: ' + (quote ? quote.quoteTimestamp : 'Unknown'),
        'Market reference: ' + (estimate ? moneyWithCents(estimate.marketCents) : 'Unknown'),
        'Cash estimate (85%): ' + (estimate ? moneyWithCents(estimate.cashCents) : 'Unknown'),
        'Trade estimate (90%): ' + (estimate ? moneyWithCents(estimate.tradeCents) : 'Unknown'),
        'Selected ring: ' + (ring ? ring.title : 'Not selected'),
        'Product ID: ' + (ring ? ring.productId : 'Not applicable'),
        'Variant ID: ' + (ring ? ring.variantId : 'Not applicable'),
        'SKU: ' + (ring ? ring.sku : 'Not applicable'),
        'Options: ' + (ring && ring.options.length ? ring.options.join(' · ') : 'Not applicable'),
        'Retail price: ' + (ring ? formatCurrencyCents(ring.priceCents, ring.currency) + ' ' + ring.currency + ' (' + ring.priceCents + ' minor units)' : 'Not applicable'),
        'Product URL: ' + (ring && ring.url ? window.location.origin + ring.url : 'Not applicable'),
        'Estimated amount to add before tax: ' + (contribution ? moneyWithCents(contribution.additionalPaymentCents) : 'Unknown'),
        'Unresolved excess estimated credit: ' + (contribution && contribution.unresolvedExcessCents > 0 ? moneyWithCents(contribution.unresolvedExcessCents) : 'Not applicable'),
        'Additional cash budget: ' + (tradeContext.additionalBudget || 'Not specified'),
        'FERE must revalidate the gold, quote, selected product, availability, price, applicable tax, and final transaction.'
      ];
    }

    function renderTradeContext() {
      const panel = root.querySelector('[data-fcrb-trade-context]');
      if (!panel) return;
      refreshTradeContext();
      panel.hidden = !tradeContext;
      if (!tradeContext) {
        setTradeField('origin', 'Not applicable');
        setTradeField('path', 'Not applicable');
        ['weight', 'karat', 'spot', 'source', 'timestamp', 'market', 'cash', 'trade', 'amountToAdd'].forEach(function (key) {
          setTradeField(key, 'Unknown');
        });
        setTradeField('status', 'Unavailable');
        ['ringTitle', 'productId', 'variantId', 'sku', 'ringOptions', 'ringPrice', 'ringCurrency', 'ringUrl', 'excessCredit'].forEach(function (key) {
          setTradeField(key, 'Not applicable');
        });
        setTradeField('additionalBudget', 'Not specified');
        return;
      }

      const estimate = tradeContext.estimate;
      const quote = tradeContext.quote;
      const ring = tradeContext.ring;
      const contribution = tradeContext.contribution;
      setTradeField('origin', 'Sell Your Gold');
      setTradeField('path', tradeContext.path);
      setTradeField('weight', tradeContext.gold.weightGrams === null ? 'Unknown' : String(tradeContext.gold.weightGrams));
      setTradeField('karat', tradeContext.gold.karat === null ? 'Unknown' : tradeContext.gold.karat + 'K');
      setTradeField('status', quote ? (quote.status === 'fresh' ? 'Live when carried' : 'Last available / stale') : 'Unavailable; revalidation required');
      setTradeField('spot', quote ? canonicalSpotUsd(quote.spotUsd) + ' USD per troy ounce' : 'Unknown');
      setTradeField('source', quote ? quote.source + ' (' + quote.provider + ')' : 'Unknown');
      setTradeField('timestamp', quote ? quote.quoteTimestamp : 'Unknown');
      setTradeField('market', estimate ? moneyWithCents(estimate.marketCents) : 'Unknown');
      setTradeField('cash', estimate ? moneyWithCents(estimate.cashCents) : 'Unknown');
      setTradeField('trade', estimate ? moneyWithCents(estimate.tradeCents) : 'Unknown');
      setTradeField('ringTitle', ring?.title || 'Not applicable');
      setTradeField('productId', ring?.productId || 'Not applicable');
      setTradeField('variantId', ring?.variantId || 'Not applicable');
      setTradeField('sku', ring?.sku || 'Not applicable');
      setTradeField('ringOptions', ring?.options?.join(' · ') || 'Not applicable');
      setTradeField('ringPrice', ring ? formatCurrencyCents(ring.priceCents, ring.currency) + ' ' + ring.currency + ' (' + ring.priceCents + ' minor units)' : 'Not applicable');
      setTradeField('ringCurrency', ring?.currency || 'Not applicable');
      setTradeField('ringUrl', ring?.url ? window.location.origin + ring.url : 'Not applicable');
      setTradeField('amountToAdd', contribution ? moneyWithCents(contribution.additionalPaymentCents) : 'Unknown');
      setTradeField('excessCredit', contribution && contribution.unresolvedExcessCents > 0 ? moneyWithCents(contribution.unresolvedExcessCents) : 'Not applicable');
      setTradeField('additionalBudget', tradeContext.additionalBudget || 'Not specified');

      const gold = panel.querySelector('[data-fcrb-trade-gold]');
      const credit = panel.querySelector('[data-fcrb-trade-credit]');
      const selectedRing = panel.querySelector('[data-fcrb-trade-ring]');
      const budget = panel.querySelector('[data-fcrb-trade-budget]');
      const status = panel.querySelector('[data-fcrb-trade-status]');
      if (gold) gold.textContent = tradeContext.gold.weightGrams === null || tradeContext.gold.karat === null
        ? 'Details to be confirmed'
        : tradeContext.gold.weightGrams.toLocaleString('en-US') + ' g · ' + tradeContext.gold.karat + 'K';
      if (credit) credit.textContent = estimate ? moneyFromCents(estimate.tradeCents) + ' USD' : 'Current estimate unavailable';
      if (selectedRing) selectedRing.textContent = ring ? ring.title + (ring.options.length ? ', ' + ring.options.join(' · ') : '') : 'Not selected';
      if (budget) budget.textContent = tradeContext.additionalBudget || 'Not specified';
      if (status) status.textContent = quote
        ? (quote.status === 'fresh'
          ? 'Carried from your recent estimate. FERE will revalidate it before any final offer.'
          : 'This is the last available estimate, not a live price. FERE will revalidate it before any final offer.')
        : 'Your gold details are saved, but the prior quote is no longer usable. Return to Sell Your Gold for a current estimate or continue for personal guidance.';
    }

    function removeTradeContext() {
      tradeContext = null;
      try { window.sessionStorage.removeItem(TradeCore.STORAGE_KEY); } catch (_error) {}
      renderTradeContext();
      updateSummaryInput();
      const currentHeading = steps[currentStep]?.querySelector('legend, h3');
      if (currentHeading) {
        currentHeading.setAttribute('tabindex', '-1');
        currentHeading.focus({ preventScroll: true });
      }
    }

    function rememberContactValue(field) {
      if (!field || !contactValueNames.includes(field.name)) return;
      contactValueCache.set(field.name, String(field.value || ''));
    }

    function restoreContactValues() {
      contactValueNames.forEach(function (name) {
        const field = form.querySelector('[name="' + CSS.escape(name) + '"]');
        if (field && contactValueCache.has(name)) field.value = contactValueCache.get(name);
      });
    }

    root.querySelectorAll('[data-fcrb-input]').forEach(function (input) {
      const key = input.dataset.fcrbInput;
      state[key] = input.value || '';
    });

    const allowedStoneTypes = ['Lab-grown diamond', 'Natural Diamond'];
    if (state.stoneType && !allowedStoneTypes.includes(state.stoneType)) {
      state.stoneType = '';
      const staleStoneInput = root.querySelector('[data-fcrb-input="stoneType"]');
      if (staleStoneInput) staleStoneInput.value = '';
    }

    function setError(message) {
      stepError.textContent = message || '';
    }

    function selectChoice(button) {
      const field = button.dataset.fcrbField;
      const value = button.dataset.value;
      if (!field) return;

      root.querySelectorAll('[data-fcrb-field]').forEach(function (candidate) {
        if (candidate.dataset.fcrbField === field) {
          const selected = candidate === button;
          candidate.classList.toggle('is-selected', selected);
          candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');
        }
      });

      state[field] = value;
      const input = root.querySelector('[data-fcrb-input="' + field + '"]');
      if (input) input.value = value;

      if (field === 'shape') updateStoneIcon(button);
      syncConditionalFields();
      renderEstimate();
      publishBuilderEvent('fere_builder_started', { source: 'custom_ring_builder' }, 'builder_started');
      if (field === 'stoneType' && value === 'Natural Diamond') {
        publishBuilderEvent('fere_natural_quote_path', { source: 'custom_ring_builder' }, 'natural_quote_path');
      }
      setError('');
      updateSummary();
      syncContinueState();
      requestNavigationViewport();
    }

    function updateStoneIcon(sourceButton) {
      const icon = sourceButton.querySelector('.fcrb-shape-icon');
      if (!icon || !summaryStone) return;
      summaryStone.replaceChildren(icon.cloneNode(true));
    }

    function createSummaryRow(label, value) {
      const wrapper = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      wrapper.className = 'fcrb-summary__row';
      term.textContent = label;
      description.textContent = value;
      wrapper.append(term, description);
      return wrapper;
    }

    function formatCarat(value) {
      const parsed = parseCaratWeight(value);
      if (parsed === null) return String(value || '');
      return parsed === 1 ? '1 carat' : parsed.toLocaleString('en-US', { maximumFractionDigits: 1 }) + ' carats';
    }

    function formatRingSize() {
      if (state.ringSize !== 'Custom size') return state.ringSize || '';
      return state.ringSizeCustom ? 'US ' + state.ringSizeCustom + ' · Custom' : 'Custom size';
    }

    function displayFieldValue(key) {
      if (key === 'carat') return formatCarat(state[key]);
      if (key === 'ringSize') return formatRingSize();
      return state[key];
    }

    function updateSummary() {
      if (!summaryList) return;
      summaryList.replaceChildren();
      const activeFields = SUMMARY_FIELDS.filter(function (key) { return Boolean(state[key]); });

      if (!activeFields.length) {
        const empty = document.createElement('p');
        empty.className = 'fcrb-summary__empty';
        empty.textContent = 'Your selections will appear here as the ring takes shape.';
        summaryList.append(empty);
        summaryTitle.textContent = 'Waiting for your first choice';
      } else {
        activeFields.forEach(function (key) {
          summaryList.append(createSummaryRow(FIELD_LABELS[key], displayFieldValue(key)));
        });
        const titleParts = [];
        if (state.shape) titleParts.push(state.shape);
        if (state.stoneType && !state.stoneType.startsWith('Not sure')) {
          titleParts.push(state.stoneType === 'Lab-grown diamond' ? 'Lab-Grown Diamond' : state.stoneType);
        }
        summaryTitle.textContent = titleParts.length ? titleParts.join(' · ') : 'Your custom ring';
      }

      updateSummaryInput();
    }

    function updateSummaryInput() {
      if (!summaryInput) return;
      const lines = [];
      Object.keys(FIELD_LABELS).forEach(function (key) {
        if (state[key]) {
          const value = key === 'carat'
            ? formatCarat(state[key])
            : key === 'ringSize'
              ? formatRingSize()
              : state[key];
          lines.push(FIELD_LABELS[key] + ': ' + value);
        }
      });
      const scopeDisclosure = estimateScopeDisclosure(state);
      if (scopeDisclosure) lines.push('Estimate scope: ' + scopeDisclosure);
      const inspiration = form.querySelector('[name="contact[Inspiration links]"]');
      const notes = form.querySelector('[name="contact[Additional notes]"]');
      if (inspiration && inspiration.value.trim()) lines.push('Inspiration: ' + inspiration.value.trim());
      if (notes && notes.value.trim()) lines.push('Notes: ' + notes.value.trim());
      summaryInput.value = lines.join('\n');
      const bodyInput = form.querySelector('[data-fcrb-contact-body]');
      if (bodyInput) {
        bodyInput.value = [
          'FERE CUSTOM RING REQUEST',
          '',
          'RING PREFERENCES',
          lines.length ? lines.join('\n') : 'No ring preferences supplied.',
          '',
          ...tradeSummaryLines(),
          '',
          'This is a consultation request, not a booked appointment, approved trade credit, price lock, or final quote.'
        ].join('\n');
      }
    }

    function restoreReturnedRequestFromBody() {
      if (!returnedFormBody.startsWith('FERE CUSTOM RING REQUEST\n')) return false;

      const marker = 'RING PREFERENCES\n';
      const markerIndex = returnedFormBody.indexOf(marker);
      if (markerIndex < 0) return false;
      const remainder = returnedFormBody.slice(markerIndex + marker.length);
      const boundary = remainder.search(/\n\n(?:GOLD TRADE CONTEXT:|This is a consultation request)/);
      const preferenceSection = boundary >= 0 ? remainder.slice(0, boundary) : remainder;
      const labels = [...Object.values(FIELD_LABELS), 'Estimate scope', 'Inspiration', 'Notes'];
      const escapedLabels = labels
        .map(function (label) { return label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); })
        .join('|');
      const values = new Map();
      const matcher = new RegExp('(?:^|\\n)(' + escapedLabels + '):\\s*([\\s\\S]*?)(?=\\n(?:' + escapedLabels + '):\\s|$)', 'g');
      let match;
      while ((match = matcher.exec(preferenceSection)) !== null) {
        values.set(match[1], match[2].trim().slice(0, 5000));
      }

      Object.keys(FIELD_LABELS).forEach(function (key) {
        let recovered = values.get(FIELD_LABELS[key]);
        if (!recovered) return;
        if (key === 'carat') recovered = recovered.replace(/\s*carats?$/i, '');
        if (key === 'ringSize' && /^US\s+[\d.]+\s+·\s+Custom$/i.test(recovered)) recovered = 'Custom size';
        const legacyAliases = {
          bandStyle: { Plain: 'Plain Band', 'Pavé': 'Diamond Band' },
          bandProfile: { Rounded: 'Classic Rounded', 'Knife edge': 'Knife Edge' }
        };
        recovered = legacyAliases[key]?.[recovered] || recovered;
        const choice = Array.from(root.querySelectorAll('[data-fcrb-field="' + key + '"]'))
          .find(function (candidate) { return candidate.dataset.value === recovered; });
        if (choice) {
          selectChoice(choice);
          return;
        }
        if (root.querySelector('[data-fcrb-field="' + key + '"]')) return;
        const field = root.querySelector('[data-fcrb-input="' + key + '"]');
        if (!field) return;
        if (field instanceof HTMLSelectElement && !Array.from(field.options).some(function (option) { return option.value === recovered; })) return;
        field.value = recovered;
        state[key] = recovered;
      });
      syncConditionalFields();

      const inspiration = form.querySelector('[name="contact[Inspiration links]"]');
      const notes = form.querySelector('[name="contact[Additional notes]"]');
      if (inspiration && values.has('Inspiration')) inspiration.value = values.get('Inspiration');
      if (notes && values.has('Notes')) notes.value = values.get('Notes');
      contactValueNames.forEach(function (name) {
        rememberContactValue(form.querySelector('[name="' + CSS.escape(name) + '"]'));
      });
      updateSummary();
      return true;
    }

    function restoreReturnedTradeContextFromBody() {
      if (!TradeCore || !returnedFormBody.includes('GOLD TRADE CONTEXT: CLIENT-SIDE UNVERIFIED ESTIMATE')) return null;
      const tradeSection = returnedFormBody.slice(returnedFormBody.indexOf('GOLD TRADE CONTEXT: CLIENT-SIDE UNVERIFIED ESTIMATE'));
      const readLine = function (label) {
        const prefix = label + ':';
        const line = tradeSection.split('\n').find(function (candidate) { return candidate.startsWith(prefix); });
        return line ? line.slice(prefix.length).trim() : '';
      };
      const weightLabel = readLine('Entered payable gold weight');
      const karatLabel = readLine('Karat');
      const spotLabel = readLine('Spot used in calculation');
      const productId = readLine('Product ID');
      const variantId = readLine('Variant ID');
      const retailPrice = readLine('Retail price');
      const productUrl = readLine('Product URL');
      const priceMatch = retailPrice.match(/\((\d+) minor units\)$/);
      const currencyMatch = retailPrice.match(/\b([A-Z]{3})\s+\(\d+ minor units\)$/);
      let normalizedProductPath = '';
      try {
        const parsedUrl = new URL(productUrl, window.location.origin);
        if (parsedUrl.origin === window.location.origin) normalizedProductPath = parsedUrl.pathname + parsedUrl.search;
      } catch (_error) {}

      const sourceLabel = readLine('Source');
      const quoteTimestamp = readLine('Quote timestamp');
      const spotUsd = Number.parseFloat(spotLabel);
      const quote = Number.isFinite(spotUsd) && quoteTimestamp && quoteTimestamp !== 'Unknown'
        ? {
            spotUsd,
            currency: 'USD',
            source: sourceLabel && sourceLabel !== 'Unknown' ? sourceLabel : 'Returned contact request',
            provider: 'returned_form',
            status: /stale|last available/i.test(readLine('Estimate status')) ? 'stale' : 'fresh',
            quoteTimestamp,
          }
        : null;
      const ring = /^\d+$/.test(productId) && /^\d+$/.test(variantId) && priceMatch && currencyMatch
        ? {
            productId,
            variantId,
            sku: readLine('SKU'),
            title: readLine('Selected ring'),
            options: readLine('Options').split(' · ').map(function (value) { return value.trim(); }).filter(Boolean),
            priceCents: Number(priceMatch[1]),
            currency: currencyMatch[1],
            url: normalizedProductPath,
          }
        : null;
      return TradeCore.createContext({
        path: 'custom-ring-trade',
        mode: 'trade',
        gold: {
          weightGrams: weightLabel.endsWith(' grams') ? weightLabel.slice(0, -6).trim() : null,
          karat: karatLabel.endsWith('K') ? karatLabel.slice(0, -1) : null,
        },
        quote,
        ring,
        additionalBudget: readLine('Additional cash budget'),
      });
    }

    function syncContactWhitespaceValidity(input) {
      if (!input || !['contact[name]', 'contact[phone]'].includes(input.name)) return;
      const value = String(input.value || '');
      const whitespaceOnly = value.length > 0 && !value.trim();
      let message = '';
      if (input.name === 'contact[name]' && whitespaceOnly) {
        message = 'Please enter your name.';
      }
      if (input.name === 'contact[phone]') {
        if (!value.trim()) message = 'Please enter your phone number.';
        else if (!isValidInternationalPhone(value)) message = 'Please enter a valid phone number with 7 to 15 digits.';
      }
      input.setCustomValidity(message);
      if (message) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
      const error = root.querySelector('[data-fcrb-contact-error="' + input.id + '"]');
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
    }

    function naturalRequestReady() {
      return state.stoneType !== 'Natural Diamond'
        || Boolean(String(state.naturalBudget || '').trim() || String(state.naturalSpecs || '').trim());
    }

    function validateCurrentStep(options) {
      const silent = options && options.silent === true;
      const step = steps[currentStep];
      if (!step) return true;

      const requiredField = step.dataset.fcrbRequired;
      if (requiredField && !state[requiredField]) {
        if (!silent) setError('Please choose one option to continue.');
        const firstChoice = step.querySelector('[data-fcrb-field="' + requiredField + '"]');
        if (!silent && firstChoice) firstChoice.focus();
        return false;
      }

      if (currentStep === 2 && !naturalRequestReady()) {
        naturalDetails?.setAttribute('aria-invalid', 'true');
        if (!silent) {
          setError('Please enter a target diamond budget or tell us the specifications you want.');
          naturalBudgetInput?.focus();
        }
        return false;
      }
      if (currentStep === 2) naturalDetails?.removeAttribute('aria-invalid');

      if (currentStep === 5 && state.engraving === 'Add engraving (+$50)' && !String(state.engravingMessage || '').trim()) {
        if (!silent) setError('Please enter the engraving message or choose no engraving.');
        if (!silent && engravingInput) engravingInput.focus();
        return false;
      }

      if (currentStep === 6 && state.ringSize === 'Custom size') {
        const customSize = Number(state.ringSizeCustom);
        if (!Number.isFinite(customSize) || customSize < 2 || customSize > 15 || Math.round(customSize * 4) !== customSize * 4) {
          if (!silent) setError('Please enter a custom US ring size in quarter-size increments, such as 6.25.');
          if (!silent && customSizeInput) customSizeInput.focus();
          return false;
        }
      }

      if (step.hasAttribute('data-fcrb-contact-step')) {
        const requiredInputs = Array.from(step.querySelectorAll('input[required], select[required], textarea[required]'));
        requiredInputs.forEach(syncContactWhitespaceValidity);
        const invalidInput = requiredInputs.find(function (input) { return !String(input.value || '').trim() || (silent && input.validity ? !input.validity.valid : !input.checkValidity()); });
        if (invalidInput) {
          if (!silent) {
            invalidInput.reportValidity();
            invalidInput.focus();
            setError('Please complete each required contact detail with valid information.');
          }
          return false;
        }
        if (!state.contactMethod) {
          if (!silent) setError('Please choose how you would prefer us to contact you.');
          const firstMethod = step.querySelector('[data-fcrb-field="contactMethod"]');
          if (!silent && firstMethod) firstMethod.focus();
          return false;
        }
      }

      return true;
    }

    // One navigation control and one validation path for touch and keyboard users.
    function syncContinueState() {
      const ready = currentStep !== REVIEW_STEP_INDEX && validateCurrentStep({ silent: true });
      nextButton.disabled = !ready;
      nextButton.setAttribute('aria-disabled', ready ? 'false' : 'true');
    }

    let navigationFrame = null;
    function syncNavigationViewport() {
      navigationFrame = null;
      if (disposed || !builder || !navigation) return;
      const viewport = window.visualViewport;
      const viewportHeight = viewport ? viewport.height : window.innerHeight;
      const viewportTop = viewport ? viewport.offsetTop : 0;
      const keyboardOffset = Math.max(0, window.innerHeight - viewportHeight - viewportTop);
      root.style.setProperty('--fcrb-keyboard-bottom', keyboardOffset + 'px');
      const rect = builder.getBoundingClientRect();
      const active = currentStep !== REVIEW_STEP_INDEX && rect.top < viewportTop + viewportHeight - 70 && rect.bottom > viewportTop + 70;
      root.classList.toggle('has-floating-navigation', active);
      if (active) root.style.setProperty('--fcrb-navigation-height', Math.ceil(navigation.getBoundingClientRect().height) + 'px');
    }
    function requestNavigationViewport() {
      if (navigationFrame === null) navigationFrame = window.requestAnimationFrame(syncNavigationViewport);
    }

    function firstMissingRequiredStep() {
      const requiredStep = steps.findIndex(function (step) {
        const requiredField = step.dataset.fcrbRequired;
        return Boolean(requiredField && !state[requiredField]);
      });
      if (requiredStep >= 0) return requiredStep;
      if (!naturalRequestReady()) return 2;
      if (state.engraving === 'Add engraving (+$50)' && !String(state.engravingMessage || '').trim()) return 5;
      if (state.ringSize === 'Custom size') {
        const customSize = Number(state.ringSizeCustom);
        if (!Number.isFinite(customSize) || customSize < 2 || customSize > 15 || Math.round(customSize * 4) !== customSize * 4) return 6;
      }
      return -1;
    }

    function showStep(index, options) {
      const config = options || {};
      const nextIndex = Math.max(0, Math.min(index, steps.length - 1));
      currentStep = nextIndex;
      root.classList.toggle('is-final-review', currentStep === REVIEW_STEP_INDEX);

      steps.forEach(function (step, stepIndex) {
        const active = stepIndex === currentStep;
        step.hidden = !active;
        step.classList.toggle('is-active', active);
        step.classList.remove('is-entering');
        if (active) {
          window.requestAnimationFrame(function () { step.classList.add('is-entering'); });
        }
      });

      if (currentStep === REVIEW_STEP_INDEX) {
        progressCount.textContent = 'Final review';
        progressBar.style.width = '100%';
        nextButton.hidden = true;
        buildReview();
        publishBuilderEvent('fere_configuration_completed', { source: 'custom_ring_builder' }, 'configuration_completed');
      } else {
        progressCount.textContent = (currentStep + 1) + ' of ' + QUESTION_STEP_COUNT;
        progressBar.style.width = ((currentStep + 1) / QUESTION_STEP_COUNT * 100) + '%';
        nextButton.hidden = false;
        nextButton.childNodes[0].nodeValue = reviewEditStep === null || (reviewEditEndStep !== null && currentStep < reviewEditEndStep)
          ? 'Continue '
          : 'Save & Return to Review ';
      }

      backButton.hidden = currentStep === 0 && reviewEditStep === null;
      syncContinueState();
      requestNavigationViewport();
      setError('');
      if (currentStep === CONTACT_STEP_INDEX) {
        window.requestAnimationFrame(function () { restoreContactValues(); syncContinueState(); });
        window.setTimeout(function () { restoreContactValues(); syncContinueState(); }, 80);
      }

      if (config.scroll) {
        scrollIntoViewRespectingMotion(builder);
      }

      if (config.focus) {
        const heading = steps[currentStep].querySelector('legend, h3');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          window.setTimeout(function () { heading.focus({ preventScroll: true }); }, 280);
        }
      }
    }

    function addReviewRow(label, value, stepIndex, endStepIndex) {
      const row = document.createElement('div');
      const category = document.createElement('span');
      const answer = document.createElement('strong');
      const edit = document.createElement('button');
      row.className = 'fcrb-review__row';
      category.textContent = label;
      answer.textContent = value || 'Not specified';
      edit.type = 'button';
      edit.textContent = 'Edit';
      edit.dataset.fcrbEditStep = String(stepIndex);
      edit.dataset.fcrbEditEndStep = String(Number.isInteger(endStepIndex) ? endStepIndex : stepIndex);
      edit.setAttribute('aria-label', 'Edit ' + label.toLowerCase());
      row.append(category, answer, edit);
      reviewList.append(row);
    }

    function compactValues(values) {
      return values.filter(Boolean).join(' · ');
    }

    function buildReview() {
      contactValueNames.forEach(function (name) {
        rememberContactValue(form.querySelector('[name="' + CSS.escape(name) + '"]'));
      });
      reviewList.replaceChildren();
      addReviewRow('Center stone', compactValues([state.shape, formatCarat(state.carat), state.stoneType]), 0, 2);
      if (state.stoneType === 'Natural Diamond') {
        if (String(state.naturalBudget || '').trim()) addReviewRow('Natural Diamond budget', state.naturalBudget.trim(), 2);
        if (String(state.naturalSpecs || '').trim()) addReviewRow('Natural Diamond specifications', state.naturalSpecs.trim(), 2);
      }
      addReviewRow('Metal', state.metal, 3);
      addReviewRow('Setting', compactValues([state.setting, state.prongs, state.headStyle]), 4);
      const scopeDisclosure = estimateScopeDisclosure(state);
      if (scopeDisclosure) addReviewRow('Estimate scope', scopeDisclosure, 4);
      addReviewRow('Band', compactValues([state.bandStyle, state.bandWidth, state.bandProfile, state.paveCoverage, state.hiddenDetails, state.engraving, state.engravingMessage]), 5);
      addReviewRow('Ring size', formatRingSize() || 'To be confirmed', 6);

      const inspiration = form.querySelector('[name="contact[Inspiration links]"]');
      addReviewRow('Inspiration', inspiration && inspiration.value.trim() ? inspiration.value.trim() : 'Images can be shared by email', INSPIRATION_STEP_INDEX);

      const name = form.querySelector('[name="contact[name]"]');
      const phone = form.querySelector('[name="contact[phone]"]');
      const email = form.querySelector('[name="contact[email]"]');
      const neededBy = form.querySelector('[name="contact[Needed-by timeframe]"]');
      const notes = form.querySelector('[name="contact[Additional notes]"]');
      addReviewRow('Consultation timing', neededBy && neededBy.value ? neededBy.value : 'To be confirmed', CONTACT_STEP_INDEX);
      if (notes && notes.value.trim()) addReviewRow('Additional notes', notes.value.trim(), CONTACT_STEP_INDEX);
      reviewContact.replaceChildren();
      const contactCopy = document.createElement('p');
      contactCopy.textContent = [
        name ? name.value.trim() : '',
        phone ? phone.value.trim() : '',
        email ? email.value.trim() : '',
        state.contactMethod ? 'Preferred: ' + state.contactMethod : ''
      ].filter(Boolean).join(' · ');
      reviewContact.append(contactCopy);
      const natural = state.stoneType === 'Natural Diamond';
      if (reviewLead) {
        reviewLead.textContent = natural
          ? 'Natural Diamonds are not priced online. Review your sourcing details, then send your request for a personal quote.'
          : 'Review the details and any available starting estimate below, then send your selections to FERE for personal review.';
      }
      if (certificationTitle) certificationTitle.textContent = natural ? 'Natural Diamond documentation' : 'Lab-Grown center documentation';
      if (certificationCopy) {
        certificationCopy.textContent = natural
          ? 'The GIA grading report for your personally sourced Natural Diamond will be provided with the stone.'
          : 'The original IGI grading report is included for the principal Lab-Grown center stone.';
      }
      renderEstimate();
      updateSummaryInput();
    }

    function toggleMobileSummary(forceOpen) {
      if (!summary || !mobileSummaryButton) return;
      const open = typeof forceOpen === 'boolean'
        ? forceOpen
        : mobileSummaryButton.getAttribute('aria-expanded') !== 'true';
      if (!open && summary.contains(document.activeElement)) mobileSummaryButton.focus({ preventScroll: true });
      summary.classList.toggle('is-mobile-open', open);
      mobileSummaryButton.setAttribute('aria-expanded', open ? 'true' : 'false');
      mobileSummaryButton.querySelector('span:first-child').textContent = open ? 'Close summary' : 'View your ring';
      document.body.classList.toggle('fcrb-summary-open', open);
      syncMobileSummaryAccessibility();
      if (open) {
        window.setTimeout(function () { summaryTitle?.focus({ preventScroll: true }); }, 260);
      }
    }

    function setMobileSummaryBackgroundInert(open) {
      if (!open) {
        mobileSummaryInertedElements.forEach(function (element) { element.removeAttribute('inert'); });
        mobileSummaryInertedElements = [];
        return;
      }
      if (mobileSummaryInertedElements.length || !summary || !mobileSummaryButton) return;
      const activeBranches = new Set();
      [summary, mobileSummaryButton].forEach(function (element) {
        for (let current = element; current && current !== document.body; current = current.parentElement) {
          activeBranches.add(current);
        }
      });
      function inertInactiveBranches(parent) {
        Array.from(parent.children).forEach(function (child) {
          if (child === summary || child === mobileSummaryButton) return;
          if (activeBranches.has(child)) {
            inertInactiveBranches(child);
          } else if (!child.hasAttribute('inert')) {
            child.setAttribute('inert', '');
            mobileSummaryInertedElements.push(child);
          }
        });
      }
      inertInactiveBranches(document.body);
    }

    function syncMobileSummaryAccessibility() {
      if (!summary || !mobileSummaryButton) return;
      if (!mobileSummaryMedia.matches) {
        summary.removeAttribute('aria-hidden');
        summary.removeAttribute('inert');
        summary.removeAttribute('role');
        summary.removeAttribute('aria-modal');
        setMobileSummaryBackgroundInert(false);
        return;
      }
      const open = mobileSummaryButton.getAttribute('aria-expanded') === 'true';
      summary.setAttribute('role', 'dialog');
      summary.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) summary.setAttribute('aria-modal', 'true');
      else summary.removeAttribute('aria-modal');
      summary.toggleAttribute('inert', !open);
      setMobileSummaryBackgroundInert(open);
    }

    function summaryFocusableElements() {
      if (!summary) return [];
      return Array.from(summary.querySelectorAll(mobileSummaryFocusSelector))
        .filter(function (element) { return !element.closest('[inert]') && element.getClientRects().length > 0; });
    }

    function documentKeydownHandler(event) {
      const open = mobileSummaryMedia.matches && summary?.classList.contains('is-mobile-open');
      if (!open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        toggleMobileSummary(false);
        mobileSummaryButton?.focus();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = summaryFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        summaryTitle?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (focusable.length === 1
        || !summary.contains(active)
        || active === summaryTitle
        || (!event.shiftKey && active === last)
        || (event.shiftKey && active === first)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }

    root.addEventListener('click', function (event) {
      const choice = event.target.closest('[data-fcrb-field]');
      if (choice && root.contains(choice)) {
        selectChoice(choice);
        return;
      }

      const consultation = event.target.closest('[data-fcrb-consultation]');
      if (consultation && root.contains(consultation)) {
        publishBuilderEvent('fere_consultation_cta_clicked', {
          source: 'custom_ring_builder',
          context: consultation.dataset.fcrbConsultationContext || 'custom'
        }, 'consultation:' + (consultation.dataset.fcrbConsultationContext || 'custom'));
      }

      const editButton = event.target.closest('[data-fcrb-edit-step]');
      if (editButton) {
        reviewEditStep = Number(editButton.dataset.fcrbEditStep);
        reviewEditEndStep = Number(editButton.dataset.fcrbEditEndStep || editButton.dataset.fcrbEditStep);
        showStep(reviewEditStep, { scroll: true, focus: true });
      }
    });

    nextButton.addEventListener('click', function () {
      if (!validateCurrentStep()) return;
      const missingStep = firstMissingRequiredStep();
      if (currentStep === CONTACT_STEP_INDEX && missingStep >= 0) {
        showStep(missingStep, { scroll: true, focus: true });
        setError('Please rebuild the required ring preferences before final review.');
        return;
      }
      if (reviewEditStep !== null) {
        if (reviewEditEndStep !== null && currentStep < reviewEditEndStep) {
          showStep(currentStep + 1, { scroll: true, focus: true });
          return;
        }
        reviewEditStep = null;
        reviewEditEndStep = null;
        showStep(REVIEW_STEP_INDEX, { scroll: true, focus: true });
        return;
      }
      showStep(currentStep + 1, { scroll: true, focus: true });
    });

    backButton.addEventListener('click', function () {
      if (reviewEditStep !== null) {
        if (currentStep > reviewEditStep) {
          showStep(currentStep - 1, { scroll: true, focus: true });
          return;
        }
        reviewEditStep = null;
        reviewEditEndStep = null;
        showStep(REVIEW_STEP_INDEX, { scroll: true, focus: true });
        return;
      }
      showStep(currentStep - 1, { scroll: true, focus: true });
    });

    root.querySelectorAll('[data-fcrb-start]').forEach(function (startButton) {
      startButton.addEventListener('click', function () {
        publishBuilderEvent('fere_builder_started', { source: 'custom_ring_builder' }, 'builder_started');
        window.setTimeout(function () {
          const firstLegend = steps[0].querySelector('legend');
          if (firstLegend) {
            firstLegend.setAttribute('tabindex', '-1');
            firstLegend.focus({ preventScroll: true });
          }
        }, 500);
      });
    });

    form.addEventListener('input', function (event) {
      rememberContactValue(event.target);
      syncContactWhitespaceValidity(event.target);
      const key = event.target && event.target.dataset ? event.target.dataset.fcrbInput : '';
      if (key) {
        state[key] = event.target.value || '';
        syncConditionalFields();
        renderEstimate();
        updateSummary();
      } else {
        updateSummaryInput();
      }
      setError('');
      syncContinueState();
    });

    form.addEventListener('change', function (event) {
      rememberContactValue(event.target);
      syncContactWhitespaceValidity(event.target);
      const key = event.target && event.target.dataset ? event.target.dataset.fcrbInput : '';
      if (!key) { syncContinueState(); return; }
      state[key] = event.target.value || '';
      syncConditionalFields();
      renderEstimate();
      setError('');
      updateSummary();
      syncContinueState();
    });

    form.addEventListener('submit', function (event) {
      // An implicit Enter must reach review, never send an unseen request.
      if (currentStep !== REVIEW_STEP_INDEX) {
        event.preventDefault();
        nextButton.click();
        return;
      }
      renderTradeContext();
      updateSummaryInput();
      const missingStep = firstMissingRequiredStep();
      if (missingStep >= 0) {
        event.preventDefault();
        showStep(missingStep, { scroll: true, focus: true });
        setError('Please complete this required ring preference before sending.');
        return;
      }
      const contactStepIndex = CONTACT_STEP_INDEX;
      const previousStep = currentStep;
      currentStep = contactStepIndex;
      const validContact = validateCurrentStep();
      currentStep = previousStep;
      if (!validContact || !form.checkValidity()) {
        event.preventDefault();
        showStep(contactStepIndex, { scroll: true, focus: true });
        return;
      }
      if (form.dataset.fcrbSubmitting === 'true'
        || (pendingSubmitEvent && !pendingSubmitEvent.defaultPrevented)) {
        event.preventDefault();
        return;
      }
      pendingSubmitEvent = event;
      try {
        const submittedTotal = estimateTotal?.textContent?.trim() || '';
        const submittedAfterCredit = estimateAfterCredit?.textContent?.trim() || '';
        const estimateValid = state.stoneType === 'Lab-grown diamond'
          && /^\$[\d,]+$/.test(submittedTotal);
        const submittedOfferEnabled = offerEnabled
          && estimateValid
          && /^\$[\d,]+$/.test(submittedAfterCredit);
        const requestId = window.crypto?.randomUUID?.()
          || Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
        const returnPath = new URL(window.location.href).pathname;
        form.dataset.fcrbSubmissionId = requestId;
        safeSessionStorage()?.setItem(SUBMISSION_STORAGE_KEY, JSON.stringify({
          status: 'pending',
          requestId: requestId,
          returnPath: returnPath,
          submittedAt: authoritativeNowMs(),
          offerEndsAt: submittedOfferEnabled ? Number(offerWindowEndMs) : null,
          savedAt: Date.now(),
          stoneType: state.stoneType || '',
          ring: Object.fromEntries(Object.keys(FIELD_LABELS)
            .filter(function (key) { return key !== 'contactMethod' && key !== 'neededBy'; })
            .map(function (key) { return [key, String(state[key] || '').slice(0, 300)]; })),
          estimateValid: estimateValid,
          offerEnabled: submittedOfferEnabled,
          total: estimateValid ? submittedTotal : '',
          afterCredit: submittedOfferEnabled ? submittedAfterCredit : ''
        }));
      } catch (_error) {}
      window.setTimeout(function () {
        if (disposed || pendingSubmitEvent !== event) return;
        if (event.defaultPrevented) {
          pendingSubmitEvent = null;
          return;
        }
        form.dataset.fcrbSubmitting = 'true';
        form.querySelectorAll('[data-fcrb-submit]').forEach(function (submit) {
          submit.disabled = true;
          submit.setAttribute('aria-disabled', 'true');
          submit.textContent = 'Sending Request…';
        });
      }, 0);
    });

    if (mobileSummaryButton) {
      mobileSummaryButton.addEventListener('click', function () { toggleMobileSummary(); });
    }
    if (summaryCloseButton) {
      summaryCloseButton.addEventListener('click', function () {
        toggleMobileSummary(false);
        mobileSummaryButton?.focus();
      });
    }
    const mobileSummaryMediaHandler = function () {
      if (!mobileSummaryMedia.matches) {
        summary?.classList.remove('is-mobile-open');
        mobileSummaryButton?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('fcrb-summary-open');
      }
      syncMobileSummaryAccessibility();
    };
    mobileSummaryMedia.addEventListener?.('change', mobileSummaryMediaHandler);
    syncMobileSummaryAccessibility();
    window.addEventListener('scroll', requestNavigationViewport, { passive: true });
    window.addEventListener('resize', requestNavigationViewport, { passive: true });
    window.visualViewport?.addEventListener('resize', requestNavigationViewport, { passive: true });
    window.visualViewport?.addEventListener('scroll', requestNavigationViewport, { passive: true });

    document.addEventListener('keydown', documentKeydownHandler);


    const requestedShape = new URLSearchParams(window.location.search).get('shape');
    if (requestedShape) {
      const requestedChoice = Array.from(root.querySelectorAll('[data-fcrb-field="shape"]')).find(function (choice) {
        return (choice.dataset.value || '').toLowerCase() === requestedShape.toLowerCase();
      });
      if (requestedChoice) selectChoice(requestedChoice);
    }

    root.querySelector('[data-fcrb-trade-remove]')?.addEventListener('click', removeTradeContext);
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      if (restoreSubmittedRequest(root)) return;
      void syncServerClock().then(function () {
        renderOfferTimer();
        void ensureFreshGoldQuote();
      });
      pendingSubmitEvent = null;
      syncContinueState();
      requestNavigationViewport();
      delete form.dataset.fcrbSubmitting;
      form.querySelectorAll('[data-fcrb-submit]').forEach(function (submit) {
        submit.disabled = false;
        submit.setAttribute('aria-disabled', 'false');
        submit.replaceChildren(document.createTextNode('Send My Ring Request '));
        const arrow = document.createElement('span');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '→';
        submit.append(arrow);
      });
    });

    const returnedTradeContext = restoreReturnedTradeContextFromBody();
    if (returnedTradeContext) {
      tradeContext = returnedTradeContext;
      try { TradeCore.writeContext(window.sessionStorage, returnedTradeContext); } catch (_error) {}
    }
    restoreReturnedRequestFromBody();
    syncConditionalFields();
    if (root.querySelector('[data-fcrb-form-errors]')) {
      showStep(CONTACT_STEP_INDEX, { scroll: true, focus: true });
    } else {
      showStep(0);
    }
    renderTradeContext();
    updateSummary();
    void syncServerClock().then(function () { void ensureFreshGoldQuote(); });

    const visibilityHandler = function () {
      if (document.visibilityState !== 'visible') return;
      void syncServerClock().then(function () {
        renderOfferTimer();
        void ensureFreshGoldQuote();
      });
    };
    const sectionUnloadHandler = function (event) {
      if (event.target !== root && !event.target?.contains?.(root)) return;
      disposed = true;
      if (offerTimerId !== null) window.clearInterval(offerTimerId);
      offerTimerId = null;
      if (goldQuoteRefreshTimerId !== null) window.clearTimeout(goldQuoteRefreshTimerId);
      goldQuoteRefreshTimerId = null;
      quoteAbortControllers.forEach(function (controller) { controller.abort(); });
      quoteAbortControllers.clear();
      document.removeEventListener('visibilitychange', visibilityHandler);
      document.removeEventListener('shopify:section:unload', sectionUnloadHandler);
      document.removeEventListener('keydown', documentKeydownHandler);
      mobileSummaryMedia.removeEventListener?.('change', mobileSummaryMediaHandler);
      window.removeEventListener('scroll', requestNavigationViewport);
      window.removeEventListener('resize', requestNavigationViewport);
      window.visualViewport?.removeEventListener('resize', requestNavigationViewport);
      window.visualViewport?.removeEventListener('scroll', requestNavigationViewport);
      document.body.classList.remove('fcrb-summary-open');
      setMobileSummaryBackgroundInert(false);
      if (navigationFrame !== null) window.cancelAnimationFrame(navigationFrame);
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    document.addEventListener('shopify:section:unload', sectionUnloadHandler);
  }

  function initAll(scope) {
    fixOurStoryLinks();
    (scope || document).querySelectorAll('[data-fcrb-root]').forEach(initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();


// Preserve Custom Ring contact details across Final Review edits and DOM refreshes.
(() => {
  const CONTACT_NAMES = [
    'contact[Inspiration links]',
    'contact[name]',
    'contact[phone]',
    'contact[email]',
    'contact[Needed-by timeframe]',
    'contact[Preferred contact method]',
    'contact[Additional notes]'
  ];

  const init = (root) => {
    if (!root || root.dataset.fereContactPersistence === 'true') return;
    const form = root.querySelector('#FereCustomRingRequestForm');
    if (!form) return;

    root.dataset.fereContactPersistence = 'true';
    const values = new Map();
    let restoreQueued = false;

    const contactFields = () => CONTACT_NAMES
      .map((name) => form.querySelector('[name="' + CSS.escape(name) + '"]'))
      .filter(Boolean);

    const store = () => {
      contactFields().forEach((field) => {
        const value = String(field.value || '');
        if (value || !values.has(field.name)) values.set(field.name, value);
      });
    };

    const restore = () => {
      restoreQueued = false;
      contactFields().forEach((field) => {
        const saved = values.get(field.name);
        if (saved && !String(field.value || '').trim()) {
          field.value = saved;
        }
      });
      syncFinalAction();
    };

    const scheduleRestore = () => {
      if (restoreQueued) return;
      restoreQueued = true;
      requestAnimationFrame(restore);
    };

    const requiredContactFields = () =>
      [...form.querySelectorAll('input[required], textarea[required], select[required]')]
        .filter((field) => CONTACT_NAMES.includes(field.name));

    const contactIsValid = () =>
      requiredContactFields().every((field) => String(field.value || '').trim() && field.checkValidity());

    const syncFinalAction = () => {
      const disabled = !contactIsValid() || form.dataset.fcrbSubmitting === 'true';
      form.querySelectorAll('[data-fcrb-submit]').forEach((action) => {
        action.disabled = disabled;
        action.setAttribute('aria-disabled', String(disabled));
      });
    };

    form.addEventListener('input', (event) => {
      if (!CONTACT_NAMES.includes(event.target.name)) return;
      values.set(event.target.name, String(event.target.value || ''));
      syncFinalAction();
    });

    form.addEventListener('change', (event) => {
      if (!CONTACT_NAMES.includes(event.target.name)) return;
      values.set(event.target.name, String(event.target.value || ''));
      syncFinalAction();
    });

    root.addEventListener('click', (event) => {
      const action = event.target.closest('button, [role="button"], a');
      if (!action) return;
      store();
      restore();
    }, true);

    form.addEventListener('submit', (event) => {
      restore();
      if (contactIsValid()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const invalid = requiredContactFields().find((field) => !String(field.value || '').trim() || !field.checkValidity());
      invalid?.focus();
      invalid?.reportValidity();
    }, true);

    new MutationObserver(scheduleRestore).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden']
    });

    store();
    syncFinalAction();
  };

  const initAll = () => document.querySelectorAll('[data-fcrb-root]').forEach(init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
